import { useEffect, useState } from 'react'
import { useEquipmentScope } from './equipmentScope'
import { HoldTimeField, parseHoldTime } from './facility/HoldTimeField'
import { Layers } from 'lucide-react'
import { Dialog, DialogContent, DialogFooter } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { ChecklistEditor, defaultChecklistRows, type ChecklistRow } from './ChecklistEditor'
import { MaintainedField } from './MaintainedField'
import { DefaultAssigneeField } from './DefaultAssigneeField'
import { useAssignableUsers } from '@/hooks/useAssignableUsers'
import {
  DEFAULT_EQUIPMENT_PURPOSE,
  EQUIPMENT_PURPOSE_LABELS,
  type EquipmentPurpose,
} from '@/types/equipment'
import type { EquipmentGroup } from '@/types/equipment'

export type NewEquipmentTypeInput = {
  equipmentType: string
  group: EquipmentGroup
  /** What the type is for. Independent of group — see EquipmentPurpose. */
  purpose: EquipmentPurpose
  /** Whether this kind of machine is serviced on a cycle. See EquipmentTypeRow. */
  maintained: boolean
  preStartChecklist: { label: string; required: boolean }[]
  /** Technician generated PM tasks for this type are assigned to, if any. */
  defaultAssigneeId?: string
  /** Room types: hours a clean holds (Cleaning Visual). Omitted: not set. */
  cleanHoldTimeHours?: number
}

export function AddEquipmentTypeModal({
  open,
  onClose,
  onAdd,
  existingTypes,
  takenElsewhere = [],
  groups,
}: {
  open: boolean
  onClose: () => void
  onAdd: (data: NewEquipmentTypeInput) => void
  existingTypes: string[]
  /** Type names on the other page (Equipment / Cleaning): names are unique across both. */
  takenElsewhere?: string[]
  /** Group names to choose from (fetched from the API). */
  groups: string[]
}) {
  const scope = useEquipmentScope()
  const { copy } = scope
  const [equipmentType, setEquipmentType] = useState('')
  const [group, setGroup] = useState<string>('')
  // Defaults to PROCESSING, matching the column default — the common case is
  // one fewer decision, and storage is the deliberate choice.
  // A room type is always a room: the Cleaning page offers no choice.
  const defaultPurpose = scope.choosePurpose ? DEFAULT_EQUIPMENT_PURPOSE : scope.purposes[0]
  const [purpose, setPurpose] = useState<EquipmentPurpose>(defaultPurpose)
  // Defaults on, matching the column: most equipment is serviced, so the
  // exception should be the deliberate choice.
  const [maintained, setMaintained] = useState(true)
  const [checklistRows, setChecklistRows] = useState<ChecklistRow[]>([])
  const [defaultAssigneeId, setDefaultAssigneeId] = useState<string | undefined>(undefined)
  const [holdTime, setHoldTime] = useState('')
  const isRoom = scope.variant === 'room'
  const { users: assignableUsers } = useAssignableUsers()

  useEffect(() => {
    if (open) {
      setEquipmentType('')
      setGroup(groups[0] ?? '')
      // Back to the column default, like every other field here: without this,
      // one unmaintained type leaves the next dialog session pre-answered.
      setMaintained(true)
      setChecklistRows(defaultChecklistRows())
      setDefaultAssigneeId(undefined)
      setPurpose(defaultPurpose)
      setHoldTime('')
    }
  }, [open, groups, defaultPurpose])

  const trimmed = equipmentType.trim()
  const isDuplicate = existingTypes.some((t) => t.toLowerCase() === trimmed.toLowerCase())
  const isTakenElsewhere = !isDuplicate && takenElsewhere.some((t) => t.toLowerCase() === trimmed.toLowerCase())
  const holdTimeOk = !isRoom || parseHoldTime(holdTime) !== undefined
  const canSubmit = trimmed !== '' && !isDuplicate && !isTakenElsewhere && group !== '' && holdTimeOk

  const handleAdd = () => {
    if (!canSubmit) return
    const preStartChecklist = checklistRows
      .filter((r) => r.label.trim() !== '')
      .map((r) => ({ label: r.label.trim(), required: r.required }))
    onAdd({
      equipmentType: trimmed,
      group: group as EquipmentGroup,
      purpose,
      maintained,
      preStartChecklist,
      defaultAssigneeId,
      ...(isRoom && parseHoldTime(holdTime) ? { cleanHoldTimeHours: parseHoldTime(holdTime) as number } : {}),
    })
    onClose()
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className='max-h-[90vh] overflow-y-auto sm:max-w-lg'>
        <div className='flex items-start gap-3'>
          <div className='flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary'>
            <Layers className='h-5 w-5' />
          </div>
          <div>
            <h2 className='text-base font-semibold text-gray-900'>{copy.addType}</h2>
            <p className='mt-0.5 text-xs text-muted-foreground'>
              {copy.typeOnlyHint}
            </p>
          </div>
        </div>

        <div className='mt-4 space-y-3'>
          <div className='space-y-1'>
            <label className='text-xs font-medium text-gray-600'>{copy.type}</label>
            <Input
              value={equipmentType}
              onChange={(e) => setEquipmentType(e.target.value)}
              placeholder={copy.typeExample}
            />
            {isDuplicate && (
              <p className='text-[11px] text-rose-500'>That {copy.unitLower} type already exists.</p>
            )}
            {isTakenElsewhere && <p className='text-[11px] text-rose-500'>{copy.takenElsewhere}</p>}
          </div>
          <div className='space-y-1'>
            <label className='text-xs font-medium text-gray-600'>{copy.group}</label>
            <Select value={group} onValueChange={setGroup}>
              <SelectTrigger className='w-full'>
                <SelectValue placeholder='Select a group' />
              </SelectTrigger>
              <SelectContent>
                {groups.map((g) => (
                  <SelectItem key={g} value={g}>
                    {g}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {scope.choosePurpose && (
          <div className='space-y-1'>
            <label className='text-xs font-medium text-gray-600'>Purpose</label>
            <Select value={purpose} onValueChange={(v) => setPurpose(v as EquipmentPurpose)}>
              <SelectTrigger className='w-full'>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {scope.purposes.map((p) => (
                  <SelectItem key={p} value={p}>
                    {EQUIPMENT_PURPOSE_LABELS[p]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className='text-[11px] text-muted-foreground'>{copy.purposeHelp}</p>
          </div>
          )}
          <MaintainedField value={maintained} onChange={setMaintained} />
          {isRoom && <HoldTimeField value={holdTime} onChange={setHoldTime} />}
          <DefaultAssigneeField
            value={defaultAssigneeId}
            onChange={setDefaultAssigneeId}
            users={assignableUsers}
          />
          <ChecklistEditor rows={checklistRows} onChange={setChecklistRows} />
        </div>

        <DialogFooter className='mt-5'>
          <Button variant='outline' onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleAdd} disabled={!canSubmit}>
            {copy.addType}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
